import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import TuneIcon from '@mui/icons-material/Tune'
import {
    Box,
    Button,
    Divider,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    SvgIcon,
    Typography
} from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { Link as RouterLink } from 'react-router-dom'

import { useFeatureFlags } from '@/features/feature-flags/useFeatureFlags'
import useAuth from '@/hooks/useAuth'
import { settingsItems } from '@/navConfig'
import { hasRole } from '@/utils/hasRole'

import { canAccessNavEntry } from '../../nav-access'

// Picked by route id, not position: the settings list is ordered for the sidebar,
// and taking its first entry once put "My statements" here in place of the account.
const findSettingsItem = (id) => settingsItems.find((item) => item.id === id)
const ACCOUNT_ITEM = findSettingsItem('account')
const MY_FEEDBACK_ITEM = findSettingsItem('my-feedback')

const itemSx = {
    borderRadius: 1,
    px: 1,
    py: 0.5
}

const NavLinkItem = ({ item, onClose }) => (
    <ListItemButton
        component={RouterLink}
        to={item.path}
        onClick={onClose}
        sx={itemSx}
    >
        <ListItemIcon>
            <SvgIcon fontSize="small">{item.icon}</SvgIcon>
        </ListItemIcon>
        <ListItemText
            primary={<Typography variant="body1">{item.label}</Typography>}
        />
    </ListItemButton>
)

NavLinkItem.propTypes = {
    item: PropTypes.shape({
        icon: PropTypes.node,
        label: PropTypes.string,
        path: PropTypes.string
    }).isRequired,
    onClose: PropTypes.func
}

export const AccountPopover = (props) => {
    const { anchorEl, onClose, open, onDisplayClick, ...other } = props
    const { isAuthenticated, user, activeOrg, logout } = useAuth()
    const { flags: featureFlags } = useFeatureFlags()
    // The same gates the sidebar applies: roles, organisation and platform features.
    const navAccess = useMemo(
        () => ({
            isAuthenticated,
            isStaff: user?.isStaff === true,
            user,
            activeOrg,
            featureFlags
        }),
        [isAuthenticated, user, activeOrg, featureFlags]
    )
    const visible = (item) => !!item && canAccessNavEntry(item, navAccess)
    // Docs are open to site admins and partner admins; show Help to both and send
    // each to the right landing page.
    const isSiteAdmin = !!user && hasRole(user, ['Site Admin Role'])
    const isPartnerStaff = Boolean(user?.isPartnerStaff)
    const canSeeDocs = isSiteAdmin || isPartnerStaff
    const docsHelpPath = isSiteAdmin
        ? '/docs/power/welcome'
        : '/docs/partner/guide'

    const handleLogout = useCallback(async () => {
        try {
            onClose?.()
            await logout()
        } catch (err) {
            console.error(err)
            toast.error('Unable to logout.')
        }
    }, [logout, onClose])

    return (
        <Popover
            anchorEl={anchorEl}
            anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom'
            }}
            disableScrollLock
            onClose={onClose}
            open={!!open}
            // PaperProps={{ sx: { width: 200 } }}
            {...other}
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="body1">{user.username}</Typography>
                <Typography color="text.secondary" variant="body2">
                    {user.email}
                </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 1 }}>
                {visible(ACCOUNT_ITEM) && (
                    <NavLinkItem item={ACCOUNT_ITEM} onClose={onClose} />
                )}
                <ListItemButton
                    onClick={onDisplayClick}
                    sx={{
                        borderRadius: 1,
                        px: 1,
                        py: 0.5
                    }}
                >
                    <ListItemIcon>
                        <SvgIcon fontSize="small">
                            <TuneIcon />
                        </SvgIcon>
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography variant="body1">Display</Typography>
                        }
                    />
                </ListItemButton>
                {visible(MY_FEEDBACK_ITEM) && (
                    <NavLinkItem item={MY_FEEDBACK_ITEM} onClose={onClose} />
                )}
                {canSeeDocs && (
                    <ListItemButton
                        component={RouterLink}
                        to={docsHelpPath}
                        onClick={onClose}
                        sx={{
                            borderRadius: 1,
                            px: 1,
                            py: 0.5
                        }}
                    >
                        <ListItemIcon>
                            <SvgIcon fontSize="small">
                                <HelpOutlineIcon />
                            </SvgIcon>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="body1">Help</Typography>
                            }
                        />
                    </ListItemButton>
                )}
            </Box>
            <Divider sx={{ my: '0 !important' }} />
            <Box
                sx={{
                    display: 'flex',
                    p: 1,
                    justifyContent: 'center'
                }}
            >
                <Button
                    color="inherit"
                    onClick={handleLogout}
                    size="small"
                    fullWidth
                >
                    Logout
                </Button>
            </Box>
        </Popover>
    )
}

AccountPopover.propTypes = {
    anchorEl: PropTypes.any,
    onClose: PropTypes.func,
    onDisplayClick: PropTypes.func,
    open: PropTypes.bool
}
